import { useEffect, useState } from 'react';
import { adminApi } from '../services/api';

interface WebSessionSettings {
  rememberDays: number;
  browserHours: number;
}

export function WebSessionsPage() {
  const [settings, setSettings] = useState<WebSessionSettings | null>(null);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi<{ settings: WebSessionSettings }>('/admin/web-sessions')
      .then((data) => setSettings(data.settings))
      .catch((err: unknown) => {
        setMessage(err instanceof Error ? err.message : 'Could not load session settings.');
      });
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setMessage('');
    try {
      const saved = await adminApi<{ settings: WebSessionSettings }>('/admin/web-sessions', {
        method: 'PUT',
        body: JSON.stringify({
          rememberDays: settings.rememberDays,
          browserHours: settings.browserHours,
        }),
      });
      setSettings(saved.settings);
      setMessage('Session settings saved. New website sign-ins use these lengths.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Could not save session settings.');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <p className="text-sm text-gray-500">Loading session settings…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Web sessions</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600">
          These lengths apply to the website and this admin console. The phone app stays signed in
          until the person logs out.
        </p>
      </div>

      {message ? <p className="text-sm text-gray-700">{message}</p> : null}

      <form
        className="max-w-lg space-y-4 rounded-xl border border-gray-200 bg-white p-4 text-sm"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="block">
          Stay signed in (days)
          <input
            className="mt-1 w-full rounded border px-2 py-1"
            type="number"
            min={1}
            max={90}
            value={settings.rememberDays}
            onChange={(event) =>
              setSettings({ ...settings, rememberDays: Number(event.target.value) })
            }
          />
          <span className="mt-1 block text-xs text-gray-500">
            Used when Remember me is checked. 1 to 90 days. New sign-ins start at 30 days.
          </span>
        </label>

        <label className="block">
          Browser session (hours)
          <input
            className="mt-1 w-full rounded border px-2 py-1"
            type="number"
            min={1}
            max={168}
            value={settings.browserHours}
            onChange={(event) =>
              setSettings({ ...settings, browserHours: Number(event.target.value) })
            }
          />
          <span className="mt-1 block text-xs text-gray-500">
            Used when Remember me is unchecked. The cookie ends when the browser closes, and the
            server session ends after this many hours. New sign-ins start at 7 days.
          </span>
        </label>

        <button
          type="submit"
          disabled={saving}
          className="rounded bg-gray-900 px-3 py-1.5 text-white disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </div>
  );
}
